import axios from 'axios';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import { useMainStore } from '@/stores/useMainStore';
import { useOrganizationStore } from '@/stores/useOrganizationStore';
import { useAppStore } from '@/stores/useAppStore';
import { useAuth0 } from '@/auth/index';
import { useDeAuth } from '@/auth/deAuth';
import roles from '@/constants/roles';

const writeKey = import.meta.env.VITE_SEGMENT_WRITE_KEY;
const DEScript = import.meta.env.VITE_APOLLO_URL;

const getClientIp = async () => {
    try {
        const repsonse = await axios.get('https://api.ipify.org?format=json');
        const { data } = repsonse;

        return data.ip || null;
    } catch {
        console.log('Unable to collect IP');
        return null;
    }
};

const leadingZero = (int) => {
    if (int < 10) {
        return `0${int}`;
    }
    return int.toString();
};

const getDateKey = () => {
    const now = new Date();
    const year = now.getFullYear();

    // js months are zero-based, add 1
    const month = leadingZero(now.getMonth() + 1);
    const date = leadingZero(now.getDate());
    const hour = leadingZero(now.getHours());

    return `${year}${month}${date}${hour}`;
};

const setDEAnalyticsConfig = async () => {
    const auth = useAuth0();
    const de = useDeAuth();
    const ip = await getClientIp();
    const mainStore = useMainStore();
    const employeeStore = useEmployeeStore();
    const orgStore = useOrganizationStore();
    const appStore = useAppStore();

    const versionMap = {
        unauthenticated: 2,
        de: 1,
        partner: 5,
    };

    let version = versionMap.unauthenticated;

    const { isAuthenticated: partnerAuthenticated } = auth;

    if (partnerAuthenticated) {
        version = versionMap.partner;
    } else if (de.isAuthenticated) {
        version = versionMap.de;
    }

    const baseConfig = {
        writeKey,
        ip,
        version,
        regionTLD: '.com',
    };

    let authConfig = {};

    if (version !== versionMap.unauthenticated) {
        // Guard: If appMode isn't set yet (still initializing), defer analytics setup
        if (!appStore.appMode) {
            console.warn('[Analytics] appMode not yet available, deferring analytics initialization');
            return null;
        }

        switch (appStore.appMode) {
            case roles.EDUCATOR_ROLE: {
                authConfig.id = mainStore.user.data.id ?? '';
                authConfig.username = mainStore.user.data.email ?? '';
                break;
            }
            case roles.EMPLOYEE_ROLE:
            case roles.ORGADMIN_ROLE: {
                authConfig.id = employeeStore.userData.employeeId ?? '';
                authConfig.username = employeeStore.userData.email ?? '';

                break;
            }
            default: {
                // This should rarely happen now that we guard against unset appMode
                // If it does, it likely means a new role was added but not handled here
                console.error('[Analytics] Unhandled appMode:', appStore.appMode, '- Analytics will continue with minimal config');

                // Set minimal config to allow analytics to continue without blocking the app
                authConfig.id = '';
                authConfig.username = '';
                break;
            }
        }
    }

    if (version === versionMap.partner) {
        authConfig = {
            ...authConfig,
            ...{
                accountId: orgStore.organizationId,
                accountName: orgStore.organization?.name,
            },
        };
    } else if (version === versionMap.de) {
        // getDEUser/getSite write the DE user onto mainStore.user.data - the employee store
        // only ever holds Auth0 (volunteer / org admin) users.
        const deUser = mainStore.user.data;

        // Safely extract site data with defaults
        const site = deUser.site || {};
        const { account_name: accountName = null, account_guid: accountId = null, site_guid: siteId = null, site_name: siteName = null } = site;

        const impersonation = deUser.impersonation || {};

        const { impersonated: isImpersonated = false, impersonator: impersonatorId = null } = impersonation;

        authConfig = {
            ...authConfig,
            ...{
                accountName,
                accountId,
                siteId,
                siteName,
                isImpersonated,
                impersonatorId,
            },
        };
    }

    if (version !== versionMap.unauthenticated) {
        return {
            ...baseConfig,
            ...authConfig,
        };
    }

    return baseConfig;
};

export default {
    DEScript,
    getDateKey,
    setDEAnalyticsConfig,
};
